import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import DatePickerCalendar from './DatePickerCalendar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Hero = () => {
  const [selectedDate, setSelectedDate] = useState('');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);

  const checkDate = async () => {
    if (!selectedDate) return;
    setChecking(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/check-date`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDate }),
      });
      const data = await res.json();
      setResult(data);
    } catch {
      setResult({ status: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setChecking(false);
    }
  };

  const statusColors = {
    available: 'bg-emerald-500/20 border-emerald-400 text-emerald-300',
    limited: 'bg-amber-500/20 border-amber-400 text-amber-300',
    unavailable: 'bg-red-500/20 border-red-400 text-red-300',
    error: 'bg-red-500/20 border-red-400 text-red-300',
  };

  return (
    <div data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0">
        <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover">
          <source src="https://customer-assets.emergentagent.com/job_wve-wedding-showcase/artifacts/b9pkkc6t_HERO%20VIDEO.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/80" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
        {/* Micro social proof */}
        <div className="animate-fade-up flex justify-center items-center gap-3 mb-8">
          <div className="flex -space-x-3">
            {[
              'https://customer-assets.emergentagent.com/job_wve-wedding-showcase/artifacts/4tj3d52l_Helen%20%26%20Daniel%2014-07-25%20%20009.jpg',
              'https://customer-assets.emergentagent.com/job_8d44df25-0f67-4d0d-b916-c24ae1fa8d16/artifacts/lgsxk5pq_Bonnie__Josh_SP_.jpg',
              'https://customer-assets.emergentagent.com/job_wve-wedding-showcase/artifacts/aiky4ekf_Paige__Richard_070724__625.jpg',
              'https://customer-assets.emergentagent.com/job_8d44df25-0f67-4d0d-b916-c24ae1fa8d16/artifacts/3xdtp1cg_Preview-45.jpg',
            ].map((src, i) => (
              <div key={i} className="w-9 h-9 rounded-full border-2 border-white/80 overflow-hidden">
                <img src={src} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
          <span className="text-white/90 text-sm font-medium">Trusted by 1000+ couples &amp; 8x Award Winners</span>
        </div>

        {/* Headline */}
        <h1 className="animate-fade-up delay-100 font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-white leading-[1.1] mb-6 max-w-5xl mx-auto">
          Your Memories.{' '}
          <em className="text-gold not-italic">Captured for a lifetime.</em>
        </h1>

        {/* Subheadline */}
        <p className="animate-fade-up delay-200 text-lg sm:text-xl text-white/80 mb-12 max-w-2xl mx-auto leading-relaxed">
          Award-winning cinematic wedding films and photography. Real moments. Real emotion. Yours forever.
        </p>

        {/* Date Checker Widget - THE primary CTA */}
        <div className="animate-fade-up delay-300 max-w-lg mx-auto">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <p className="text-white/90 text-sm font-medium mb-4 uppercase tracking-wider">
              Is your date still available?
            </p>
            <div className="flex gap-3">
              <DatePickerCalendar
                value={selectedDate}
                onChange={(d) => { setSelectedDate(d); setResult(null); }}
                className="flex-1"
              />
              <button
                data-testid="hero-check-date-btn"
                onClick={checkDate}
                disabled={!selectedDate || checking}
                className="bg-gold hover:bg-gold-light text-black font-semibold px-6 py-3.5 rounded-xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:hover:scale-100 flex items-center gap-2 whitespace-nowrap"
              >
                {checking ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Checking...
                  </span>
                ) : (
                  <>Check <ChevronRight className="w-4 h-4" /></>
                )}
              </button>
            </div>

            {/* Result */}
            {result && result.status && (
              <div className={`mt-4 p-4 rounded-xl border ${statusColors[result.status]} animate-fade-up`}>
                <p className="font-semibold text-sm">{result.message}</p>
                {result.urgency && <p className="text-xs mt-1 opacity-80">{result.urgency}</p>}
                <button
                  onClick={() => window.location.href = 'https://www.weddingvideo.co.uk/contact'}
                  className="mt-3 bg-gold hover:bg-gold-light text-black text-xs font-bold px-5 py-2 rounded-full transition-all hover:scale-105"
                >
                  {result.status === 'unavailable' ? 'Check Another Date' : 'Secure My Date'}
                </button>
              </div>
            )}
          </div>
          <p className="text-white/50 text-xs mt-3">No commitment. No spam. Just a quick check.</p>
        </div>
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <div className="w-5 h-8 border-2 border-white/30 rounded-full flex items-start justify-center p-1.5">
          <div className="w-1 h-2 bg-white/40 rounded-full" />
        </div>
      </div>
    </div>
  );
};

export default Hero;
